<template>
	<AssemblyLayout
		:assembly="assembly"
		:subtitle="election.title"
		:return-url="route('association.assemblies.elections.college-zone-representatives.index', assembly.id)"
		:return-label="trans('assemblies.elections.college-zone-representatives.title')"
	>
		<template v-if="election.open" #actions>
			<template v-if="election.active">
				<div class="flex items-center gap-2 text-green-600 uppercase animate-pulse">
					<i class="fa-solid fa-circle"/>
					{{ trans('general.on_air') }}
				</div>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock-open"/>
				</PrimaryButton>
			</template>
			<template v-else>
				<PrimaryButton :color="Color.Green" @click.stop="ratify">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('assemblies.elections.ratify') }}
				</PrimaryButton>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock"/>
				</PrimaryButton>
			</template>
		</template>
		<Card :title="`${trans('assemblies.seats')}: ${election.limit}`">
			<template v-if="canRatify">
				<List>
					<ElectionCandidate v-for="electionCandidate in election.candidates" :key="`election-candidate-${electionCandidate.id}`" :title="electionCandidate.candidate.user.full_name" :candidate="electionCandidate" :selected="selected" :election="election" :limit="election.limit"/>
				</List>
				<template v-if="election.closed">
					<Alert :color="Color.Green">{{ trans('assemblies.elections.ratified') }}</Alert>
					<LabeledObject>
						<template #label>{{ trans('assemblies.elections.ratification') }}</template>
						<p>{{ election.ratification }}</p>
					</LabeledObject>
				</template>
				<LabeledObject v-else required>
					<template #label>{{ trans('assemblies.elections.ratification') }}</template>
					<textarea v-model="election.ratification"/>
				</LabeledObject>
			</template>
			<Alert v-else :color="Color.Yellow">{{ trans('enrollments.accreditation.no_permissions') }}</Alert>
		</Card>
	</AssemblyLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {router} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {onMounted, type Ref, ref} from 'vue'
import {useEchoModel} from '@laravel/echo-vue'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import {type Election} from '@/Types/Elections/Election'
import AssemblyLayout from '@/Layouts/AssemblyLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type ElectionVote} from '@/Types/Elections/ElectionVote'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import ElectionCandidate from '@/Components/Elections/ElectionCandidate.vue'
import {type CollegeZoneRepresentative} from '@/Types/Association/Assemblies/CollegeZoneRepresentative'
import {type CollegeZoneRepresentativeElection} from '@/Types/Elections/Assemblies/CollegeZoneRepresentativeElection'

const props = defineProps<{
	assembly:Assembly,
	canRatify:boolean,
	selected:Array<number>,
	election:CollegeZoneRepresentativeElection,
}>()

const working:Ref<boolean> = ref(false)
const assembly:Ref<Assembly> = ref(props.assembly)
const selected:Ref<Array<number>> = ref(props.selected)
const election:Ref<CollegeZoneRepresentativeElection> = ref(props.election)

function ratify():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.assemblies.elections.college-zone-representatives.update', {assembly: assembly.value.id, election: election.value.id}), {
			selected: selected.value,
			ratification: election.value.ratification,
		}).then(({data}:{data:number|null|undefined}) => {
			showSuccess(trans('general.updated'))
			election.value.closed = true
			election.value.open = false
			if(data){
				router.get(route('association.assemblies.elections.college-zone-representatives.show', {assembly: assembly.value.id, election: data}))
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function toggle():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.assemblies.elections.college-zone-representatives.toggle.update', {assembly: assembly.value.id, election: election.value.id})).then(({data}:{data:boolean}) => {
			showSuccess(trans('general.updated'))
			election.value.active = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

onMounted(():void => {
	const assemblyEchoModel = useEchoModel('elections', election.value.id)
	assemblyEchoModel.channel().listen('VoteStored', (event:{vote:ElectionVote<Election<CollegeZoneRepresentative>, CollegeZoneRepresentative>}) => {
		if(event.vote.election_type === 'collegeZoneRepresentativeElection'){
			election.value.votes.push(event.vote)
		}
	})
})
</script>